import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/uxp-photoshop-zh-cn/uxp-photoshop-zh-cn/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "photoshop-api-changelog"
    }}>{`Photoshop API Changelog`}</h1>
    <h2 {...{
      "id": "photoshop-beta-242-february-2023"
    }}>{`Photoshop Beta (24.2 February 2023)`}</h2>
    <h3 {...{
      "id": "imaging-api-beta"
    }}>{`Imaging API Beta`}</h3>
    <p>{`We are excited to provide an entirely new capability with the `}<a parentName="p" {...{
        "href": "../media/imaging"
      }}>{`Imaging API`}</a>{`.  Anyone that has been eagerly awaiting the ability to directly get and set pixels in a document should head to the `}<a parentName="p" {...{
        "href": "../media/imaging"
      }}>{`dedicated page`}</a>{`.  Note: we are serious about the Beta label.  While this is exciting new territory, keep in mind that the API is in an early form.  We want your feedback to help guide it toward maturity.`}</p>
    <h3 {...{
      "id": "text-support"
    }}>{`Text support`}</h3>
    <p>{`The UXP DOM features a variety of new text-related APIs, that provide a more robust and consistent experience for developers. Instead of aiming for ExtendScript parity, the UXP DOM has been designed to provide a more modern and intuitive API surface. While still providing the old functionality, it's been extended to cover many features that weren't previously available.`}</p>
    <ul>
      <li parentName="ul">{`A `}<a parentName="li" {...{
          "href": "../classes/document.md#createtextlayer"
        }}>{`Document.createTextLayer`}</a>{` method was added, which allows developers to create a new text layer in a document. It accepts a `}<a parentName="li" {...{
          "href": "../objects/createoptions/textlayercreateoptions/"
        }}>{`TextLayerCreateOptions`}</a>{` object, which can be used to set the initial text content, style, and other properties.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`textItem`}</inlineCode>{` property of a Text Layer is the entrypoint for everything text-related. It provides direct access to a few properties and methods:`}
        <ul parentName="li">
          <li parentName="ul">{`contents`}</li>
          <li parentName="ul">{`isParagraphText`}</li>
          <li parentName="ul">{`isPointText`}</li>
          <li parentName="ul">{`orientation`}</li>
          <li parentName="ul">{`textClickPoint`}</li>
          <li parentName="ul">{`convertToParagraphText()`}</li>
          <li parentName="ul">{`convertToPointText()`}</li>
          <li parentName="ul">{`convertToShape()`}</li>
          <li parentName="ul">{`createWorkPath()`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Properties and methods related to features made available by Photoshop in the Character palette are now grouped in the `}<inlineCode parentName="li">{`textItem.characterStyle`}</inlineCode>{` object:`}
        <ul parentName="li">
          <li parentName="ul">{`alternateLigatures`}</li>
          <li parentName="ul">{`antiAliasMethod`}</li>
          <li parentName="ul">{`autoKerning`}</li>
          <li parentName="ul">{`baseline`}</li>
          <li parentName="ul">{`baselineShift`}</li>
          <li parentName="ul">{`capitalization`}</li>
          <li parentName="ul">{`characterAlignment`}</li>
          <li parentName="ul">{`color`}</li>
          <li parentName="ul">{`fauxBold`}</li>
          <li parentName="ul">{`fauxItalic`}</li>
          <li parentName="ul">{`font`}</li>
          <li parentName="ul">{`fractionalWidths`}</li>
          <li parentName="ul">{`fractions`}</li>
          <li parentName="ul">{`horizontalScale`}</li>
          <li parentName="ul">{`kashidas`}</li>
          <li parentName="ul">{`horizontalDiacriticPosition`}</li>
          <li parentName="ul">{`verticalDiacriticPosition`}</li>
          <li parentName="ul">{`language`}</li>
          <li parentName="ul">{`leading`}</li>
          <li parentName="ul">{`ligatures`}</li>
          <li parentName="ul">{`middleEasternDigitsType`}</li>
          <li parentName="ul">{`middleEasternTextDirection`}</li>
          <li parentName="ul">{`noBreak`}</li>
          <li parentName="ul">{`ordinals`}</li>
          <li parentName="ul">{`size`}</li>
          <li parentName="ul">{`strikeThrough`}</li>
          <li parentName="ul">{`stylisticAlternates`}</li>
          <li parentName="ul">{`swash`}</li>
          <li parentName="ul">{`titlingAlternates`}</li>
          <li parentName="ul">{`tracking`}</li>
          <li parentName="ul">{`useAutoLeading`}</li>
          <li parentName="ul">{`verticalScale`}</li>
          <li parentName="ul">{`reset()`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Properties and methods related to features made available by Photoshop in the Paragraph palette are now grouped in the `}<inlineCode parentName="li">{`textItem.paragraphStyle`}</inlineCode>{` object:`}
        <ul parentName="li">
          <li parentName="ul">{`firstLineIndent`}</li>
          <li parentName="ul">{`hyphenation`}</li>
          <li parentName="ul">{`hyphenationFeatures`}</li>
          <li parentName="ul">{`justification`}</li>
          <li parentName="ul">{`justificationFeatures`}</li>
          <li parentName="ul">{`layoutMode`}</li>
          <li parentName="ul">{`kashidaWidth`}</li>
          <li parentName="ul">{`kinsoku`}</li>
          <li parentName="ul">{`mojikumi`}</li>
          <li parentName="ul">{`leftIndent`}</li>
          <li parentName="ul">{`rightIndent`}</li>
          <li parentName="ul">{`spaceAfter`}</li>
          <li parentName="ul">{`spaceBefore`}</li>
          <li parentName="ul">{`reset()`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Properties and methods related to features made available by Photoshop in the Warp text dialog are now grouped in the `}<inlineCode parentName="li">{`textItem.warpStyle`}</inlineCode>{` object:`}
        <ul parentName="li">
          <li parentName="ul">{`style`}</li>
          <li parentName="ul">{`direction`}</li>
          <li parentName="ul">{`bend`}</li>
          <li parentName="ul">{`horizontalDistortion`}</li>
          <li parentName="ul">{`verticalDistortion`}</li>
          <li parentName="ul">{`reset()`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "uxp"
    }}>{`UXP`}</h3>
    <p>{`From the `}<a parentName="p" {...{
        "href": "../../uxp/"
      }}>{`UXP team`}</a>{` we have integrated the following update.`}</p>
    <h4 {...{
      "id": "v65-integration"
    }}>{`v6.5 Integration`}</h4>
    <h5 {...{
      "id": "new"
    }}>{`New`}</h5>
    <ul>
      <li parentName="ul">{`Allow unrestricted relative paths in require from scripts`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../../uxp/reference-js/Modules/uxp/Persistent%20File%20Storage/FileSystemProvider.md#getentrywithurlurl"
        }}>{`getEntryWithUrl`}</a>{` in LocalFileProvider`}</li>
    </ul>
    <hr></hr>
    <h2 {...{
      "id": "photoshop-241-december-2022"
    }}>{`Photoshop 24.1 (December 2022)`}</h2>
    <h3 {...{
      "id": "appgetcolorprofiles"
    }}>{`app.getColorProfiles`}</h3>
    <p>{`Color profiles are needed for the Imaging API, so a new method on the `}<inlineCode parentName="p">{`app`}</inlineCode>{` is introduced to return profile names.  At this time, only profiles for RGB and Gray are available.`}</p>
    <h3 {...{
      "id": "countitems-support"
    }}>{`CountItems support`}</h3>
    <p>{`The CountItems DOM API is now available in Photoshop.`}</p>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "../classes/countitems/"
        }}>{`CountItems collection`}</a>{` behaves like other collections at the Document level in the API. CountItems further support the following properties and methods:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`typename`}</inlineCode>{`, `}<inlineCode parentName="li">{`length`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`add()`}</inlineCode>{`, `}<inlineCode parentName="li">{`removeAll()`}</inlineCode>{`, `}<inlineCode parentName="li">{`getAll()`}</inlineCode>{`, `}<inlineCode parentName="li">{`createGroup()`}</inlineCode>{`, `}<inlineCode parentName="li">{`renameActiveGroup()`}</inlineCode>{`, `}<inlineCode parentName="li">{`removeGroupByIndex()`}</inlineCode>{`, `}<inlineCode parentName="li">{`toggleActiveGroupVisibility()`}</inlineCode>{`, `}<inlineCode parentName="li">{`activateGroupByIndex()`}</inlineCode>{`, `}<inlineCode parentName="li">{`setActiveMarkerSize()`}</inlineCode>{`, `}<inlineCode parentName="li">{`setActiveLabelSize()`}</inlineCode>{`, `}<inlineCode parentName="li">{`setActiveColor()`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "other-fixes"
    }}>{`Other fixes`}</h3>
    <h4 {...{
      "id": "we-now-throw-errors-in-more-situations-with-layer-blend-modes"
    }}>{`We now throw errors in more situations with layer blend modes`}</h4>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Passing a value that is not in [`}{`[Constants.blendMode]`}{`].`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Passing a blend mode that is not compatible with the document's color mode or bit depth. Previously, this call would fail silently.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Attempting to set the blend mode on the Background layer. Previously, doing so would convert Background to a regular layer and generate a new layer ID.  Automatic Background promotion is undesirable as a side effect in this case.  This action is not available via the UI.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "../classes/countitem"
          }}>{`CountItem class`}</a>{` implements the following properties and methods: `}</p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`typename`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`, `}<inlineCode parentName="li">{`itemIndex`}</inlineCode>{`, `}<inlineCode parentName="li">{`groupIndex`}</inlineCode>{`, `}<inlineCode parentName="li">{`position`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`move()`}</inlineCode>{`, `}<inlineCode parentName="li">{`remove()`}</inlineCode></li>
          <li parentName="ul"></li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "uxp-1"
    }}>{`UXP`}</h3>
    <p>{`From the `}<a parentName="p" {...{
        "href": "../../uxp/"
      }}>{`UXP team`}</a>{` we have integrated the following update.`}</p>
    <h4 {...{
      "id": "v64-integration"
    }}>{`v6.4 Integration`}</h4>
    <h5 {...{
      "id": "new-1"
    }}>{`New`}</h5>
    <ul>
      <li parentName="ul">{`Webviews for Panels`}</li>
      <li parentName="ul">{`Support Path Module APIs`}</li>
      <li parentName="ul">{`Script can run fsapi with full access in localFileSystem`}</li>
    </ul>
    <h5 {...{
      "id": "bugs-fixes"
    }}>{`Bugs Fixes`}</h5>
    <ul>
      <li parentName="ul">{`PluginManifest parsing / Load Plugin errors not logged in UDT`}</li>
      <li parentName="ul">{`Error shown in UDT APP logs on Plugin UnLoad`}</li>
      <li parentName="ul">{`UXP script : file session tokens are not getting created`}</li>
      <li parentName="ul">{`Error message and stack are empty in case of reject("error string") executed in script`}</li>
      <li parentName="ul">{`[Win only]`}{`-Volume is being muted automatically for 3rd video in the tutorial while opening the video in Theatre mode`}</li>
      <li parentName="ul">{`After closing the theatre mode, video's are Overlapping while scrolling up and down in DP`}</li>
      <li parentName="ul">{`Provide an API to programmatically close dialog in pop-out/theatre mode.`}</li>
      <li parentName="ul">{`Plugin Data folder shouldn't be created for scripts`}</li>
      <li parentName="ul">{`Crash on exit`}</li>
      <li parentName="ul">{`script.setResult("kSuccess") not working`}</li>
      <li parentName="ul">{`Resource Leaks in Window Dialog on UWP`}</li>
      <li parentName="ul">{`Privilege plugin fail to add in XD in certain cases`}</li>
      <li parentName="ul">{`Fixed mouse/keyboard events within Webview in XD v55 and Win 10.`}</li>
    </ul>
    <h2 {...{
      "id": "photoshop-240-october-2022"
    }}>{`Photoshop 24.0 (October 2022)`}</h2>
    <h3 {...{
      "id": "preferences"
    }}>{`Preferences`}</h3>
    <p>{`Adds API to change some of the Photoshop `}<a parentName="p" {...{
        "href": "../classes/preferences"
      }}>{`preferences`}</a>{`. Only first set of 38 preferences was added. More will follow.`}</p>
    <h3 {...{
      "id": "colorsamplers-support"
    }}>{`ColorSamplers support`}</h3>
    <p>{`The ColorSampler DOM API is now available in Photoshop.`}</p>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "../classes/colorsamplers/"
        }}>{`ColorSamplers collection`}</a>{` behaves like other collections at the Document level in the API. ColorSamplers further supports the following methods:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`colorSamplers.add()`}</inlineCode>{` `}</li>
          <li parentName="ul"><inlineCode parentName="li">{`colorSamplers.removeAll()`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "../classes/colorsampler"
        }}>{`ColorSamplers class`}</a>{` implements the following properties and methods: `}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`typename`}</inlineCode>{`: String`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`parent`}</inlineCode>{`: `}<a parentName="li" {...{
              "href": "../classes/document"
            }}>{`Document`}</a></li>
          <li parentName="ul"><inlineCode parentName="li">{`position`}</inlineCode>{`: `}<inlineCode parentName="li">{`{x: number, y: number}`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`color`}</inlineCode>{`: `}<a parentName="li" {...{
              "href": "../classes/solidcolor.md"
            }}>{`SolidColor`}</a></li>
          <li parentName="ul"><inlineCode parentName="li">{`move({x: number, y: number})`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`remove()`}</inlineCode>
            {/* - `sampleSize()`: static member, accepts values from [Constants.SampleSize](../modules/constants/#samplesize). */}
          </li>
        </ul>
      </li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "../classes/document#samplecolor"
        }}>{`Document`}</a>{` class implements a new `}<inlineCode parentName="li">{`sampleColor()`}</inlineCode>{` method that samples an `}<inlineCode parentName="li">{`{x, y}`}</inlineCode>{` position on the fly, returning a `}<a parentName="li" {...{
          "href": "../classes/solidcolor.md"
        }}>{`SolidColor`}</a>{` object without the need to create a `}<a parentName="li" {...{
          "href": "../classes/colorsampler.md"
        }}>{`ColorSampler`}</a>{` object.`}</li>
    </ul>
    <h3 {...{
      "id": "layer-comps-support"
    }}>{`Layer Comps support`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/document/#layercomps"
        }}>{`Document.layerComps`}</a>{` property is now added, giving access to similar APIs from ExtendScript to work with layer comps.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layercomps"
        }}>{`LayerComps collection`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Adds the following Layer Comp methods: `}<inlineCode parentName="li">{`add()`}</inlineCode>{`, `}<inlineCode parentName="li">{`removeAll()`}</inlineCode>{`, `}<inlineCode parentName="li">{`getByName()`}</inlineCode></li>
          <li parentName="ul">{`Create new layer comps in the document using `}<a parentName="li" {...{
              "href": "../classes/layercomps/#add"
            }}><inlineCode parentName="a">{`LayerComps.add()`}</inlineCode></a>{` method. Constructors for them can be found in `}<a parentName="li" {...{
              "href": "../classes/photoshop/"
            }}>{`app`}</a>{` object.`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layercomp"
        }}>{`LayerComp class`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Adds the following Layer Comp properties: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`comment`}</inlineCode>{`, `}<inlineCode parentName="li">{`selected`}</inlineCode>{`, `}<inlineCode parentName="li">{`appearance`}</inlineCode>{`, `}<inlineCode parentName="li">{`position`}</inlineCode>{`, `}<inlineCode parentName="li">{`visibility`}</inlineCode>{`, `}<inlineCode parentName="li">{`childComp`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Adds the following Layer Comp methods: `}<inlineCode parentName="li">{`apply()`}</inlineCode>{`, `}<inlineCode parentName="li">{`recapture()`}</inlineCode>{`, `}<inlineCode parentName="li">{`remove()`}</inlineCode>{`, `}<inlineCode parentName="li">{`resetLayerComp()`}</inlineCode>{`, `}<inlineCode parentName="li">{`duplicate()`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "guide-fixes"
    }}>{`Guide fixes`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/guide/#coordinate"
        }}>{`Guide.coordinate`}</a>{` Fixes coordinate getter when document resolution is not 72 PPI`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/guide/#coordinate"
        }}>{`Guide.coordinate`}</a>{` Fixes coordinate setter when document resolution is not 72 PPI`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/guides/#add"
        }}>{`Guides.add`}</a>{` Fixes coordinate when creating a new guide if document resolution is not 72 PPI`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/guides/#add"
        }}>{`Guides.add`}</a>{` Creating a new guide had returned an invalid, nonexistent instance. It now points to an existing guide.`}</li>
    </ul>
    <h3 {...{
      "id": "known-issues-and-workarounds"
    }}>{`Known Issues and Workarounds`}</h3>
    <ul>
      <li parentName="ul">{`A new page of `}<a parentName="li" {...{
          "href": "../known-issues"
        }}>{`Known Issues and Workarounds`}</a>{` was introduced, and aims to outline some common issues encountered by our developer community. New entries will be summarized in this changelog.`}</li>
      <li parentName="ul">{`A note about increased error throws from `}<inlineCode parentName="li">{`batchPlay`}</inlineCode>{`, specifically with the `}<inlineCode parentName="li">{`move`}</inlineCode>{` command, was added.`}</li>
    </ul>
    <h3 {...{
      "id": "layer-filter-methods"
    }}>{`Layer Filter Methods`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applysmartblur"
        }}>{`Layer.applySmartBlur(radius: number, threshold: number, blurQuality: Constants.SmartBlurQuality, mode: Constants.SmartBlurMode)`}</a>{` Applies the smart blur filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyspherize"
        }}>{`Layer.applySpherize(amount: number, mode: Constants.SpherizeMode)`}</a>{` Applies the spherize filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyunsharpmask"
        }}>{`Layer.applyUnSharpMask(amount: number, radius: number, threshold: number)`}</a>{` Applies the unsharp mask filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applywave"
        }}>{`Layer.applyWave(generatorNumber: number, minimumWavelength: number, maximumWavelength: number, minimumAmplitude: number, maximumAmplitude: number, horizontalScale: number, verticalScale: number, waveType: Constants.WaveType, undefinedAreas: Constants.UndefinedAreas, randomSeed: number)`}</a>{` Applies the wave filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyzigzag"
        }}>{`Layer.applyZigZag(amount: number, ridges: number, style: Constants.ZigZagType)`}</a>{` Applies the zigzag filter.`}</li>
    </ul>
    <h3 {...{
      "id": "other"
    }}>{`Other`}</h3>
    <ul>
      <li parentName="ul">{`Color mode validation for all filters`}</li>
      <li parentName="ul">{`Fixed Lens Flare coordinates`}</li>
      <li parentName="ul">{`Fixed file arguments for filters `}</li>
    </ul>
    <h2 {...{
      "id": "photoshop-235-august-2022"
    }}>{`Photoshop 23.5 (August 2022)`}</h2>
    <h3 {...{
      "id": "uxp-scripting"
    }}>{`UXP Scripting`}</h3>
    <p>{`Script files with extension .psjs are executed using UXP. `}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../../scripting/"
        }}>{`Overview`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../../scripting/getting-started/"
        }}>{`Quickstart Guide`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../media/uxpscripting/"
        }}>{`Ps UXP Scripting`}</a></li>
    </ul>
    <h3 {...{
      "id": "layer-filter-methods-1"
    }}>{`Layer Filter methods`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyaddnoise"
        }}>{`Layer.applyAddNoise(amount: number, distribution: Constants.NoiseDistribution, monochromatic: boolean)`}</a>{` Applies the Add Noise filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyaverage"
        }}>{`Layer.applyAverage()`}</a>{` Applies the Average filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyblur"
        }}>{`Layer.applyBlur()`}</a>{` Applies the Blur filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyblurmore"
        }}>{`Layer.applyBlurMore()`}</a>{` Applies the Blur More filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyclouds"
        }}>{`Layer.applyClouds()`}</a>{` Applies the Clouds filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applycustomfilter"
        }}>{`Layer.applyCustomFilter(characteristics: number[], scale: number, offset: number)`}</a>{` Applies the Custom filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydeinterlace"
        }}>{`Layer.applyDeInterlace(eliminateFields: Constants.EliminateFields, createFields: Constants.CreateFields)`}</a>{` Applies the De-Interlace filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydespeckle"
        }}>{`Layer.applyDespeckle()`}</a>{` Applies the Despeckle filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydifferenceclouds"
        }}>{`Layer.applyDifferenceClouds()`}</a>{` Applies the Difference Clouds filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydiffuseglow"
        }}>{`Layer.applyDiffuseGlow(graininess: number, glowAmount: number, clearAmount: number)`}</a>{` Applies the Diffuse Glow filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydisplace"
        }}>{`Layer.applyDisplace(horizontalScale: number, verticalScale: number, displacementType: Constants.DisplacementMapType, undefinedAreas: Constants.UndefinedAreas, displacementMapFile: File)`}</a>{` Applies the Displace filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applydustandscratches"
        }}>{`Layer.applyDustAndScratches(radius: number, threshold: number)`}</a>{` Applies the Dust and Scratches filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applygaussianblur"
        }}>{`Layer.applyGaussianBlur(radius: number)`}</a>{` Applies the Gaussian Blur filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyglasseffect"
        }}>{`Layer.applyGlassEffect(distortion: number, smoothness: number, scaling: number, invert: boolean, texture: Constants.TextureType, textureFile?: File)`}</a>{` Applies the Glass filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyhighpass"
        }}>{`Layer.applyHighPass(radius: number)`}</a>{` Applies the High Pass filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applylensblur"
        }}>{`Layer.applyLensBlur(source: Constants.DepthMapSource, focalDistance: number, invertDepthMask: boolean, shape: Constants.Geometry, radius: number, bladeCurvature: number, rotation: number, brightness: number, threshold: number, amount: number, distribution: Constants.NoiseDistribution, monochromatic: boolean)`}</a>{` Applies the Lens Blur filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applylensflare"
        }}>{`Layer.applyLensFlare(brightness: number, flareCenter: {x: number, y: number}, lensType: Constants.LensType)`}</a>{` Applies the Lens Flare filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applymaximum"
        }}>{`Layer.applyMaximum(radius: number, preserveShape: Constants.PreserveShape)`}</a>{` Applies the Maximum filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyminimum"
        }}>{`Layer.applyMinimum(radius: number, preserveShape: Constants.PreserveShape)`}</a>{` Applies the Minimum filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applymediannoise"
        }}>{`Layer.applyMedianNoise(radius: number)`}</a>{` Applies the Median Noise filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applymotionblur"
        }}>{`Layer.applyMotionBlur(angle: number, distance: number)`}</a>{` Applies the Motion Blur filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyntsc"
        }}>{`Layer.applyNTSC()`}</a>{` Applies the NTSC filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyoceanripple"
        }}>{`Layer.applyOceanRipple(size: number, magnitude: number)`}</a>{` Applies the Ocean Ripple filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyoffset"
        }}>{`Layer.applyOffset(horizontal: number, vertical: number, undefinedAreas: Constants.OffsetUndefinedAreas)`}</a>{` Applies the Offset filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applytwirl"
        }}>{`Layer.applyTwirl(angle: number)`}</a>{` Applies the Twirl filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applypinch"
        }}>{`Layer.applyPinch(amount: number)`}</a>{` Applies the Pinch filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applypolarcoordinates"
        }}>{`Layer.applyPolarCoordinates(conversion: Constants.PolarConversionType)`}</a>{` Applies the Polar Coordinates filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyripple"
        }}>{`Layer.applyRipple(amount: number, size: Constants.RippleSize)`}</a>{` Applies the Ripple filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applysharpen"
        }}>{`Layer.applySharpen()`}</a>{` Applies the Sharpen filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applysharpenedges"
        }}>{`Layer.applySharpenEdges()`}</a>{` Applies the Sharpen Edges filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applysharpenmore"
        }}>{`Layer.applySharpenMore()`}</a>{` Applies the Sharpen More filter.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer/#applyshear"
        }}>{`Layer.applyShear(curve: {x: number, y: number}[], undefinedArea: Constants.UndefinedAreas)`}</a>{` Applies the Shear filter.`}</li>
    </ul>
    <h2 {...{
      "id": "photoshop-234-july-2022"
    }}>{`Photoshop 23.4 (July 2022)`}</h2>
    <h3 {...{
      "id": "convertunits-method-on-the-photoshop-application-object"
    }}>{`convertUnits method on the Photoshop Application object`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/photoshop/#convertunits"
        }}>{`Application.convertUnits`}</a>{` is a method for converting the pixel values found in the UXP API to other units. For example, use this routine for converting a document's width from pixels to inches.`}</li>
    </ul>
    <h2 {...{
      "id": "photoshop-233-april-2022"
    }}>{`Photoshop 23.3 (April 2022)`}</h2>
    <h3 {...{
      "id": "paths-support"
    }}>{`Paths support`}</h3>
    <ul>
      <li parentName="ul">{`Document.pathItems property is now added, giving access to same APIs from ExtendScript to work with paths.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.pathItems`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/pathitems/"
        }}>{`PathItems`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/pathitem/"
        }}>{`PathItem`}</a>{` objects.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`PathItem.subPathItems`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/subpathitems/"
        }}>{`SubPathItems`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/subpathitem/"
        }}>{`SubPathItem`}</a>{` objects.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`SubPathItem.pathPoints`}</inlineCode>{` provides the collection class `}<a parentName="li" {...{
          "href": "../classes/pathpoints/"
        }}>{`PathPoints`}</a>{`, which is a collection of `}<a parentName="li" {...{
          "href": "../classes/pathpoint/"
        }}>{`PathPoint`}</a>{` objects.`}</li>
      <li parentName="ul">{`Create new paths in the document using `}<a parentName="li" {...{
          "href": "../classes/pathitems/#add"
        }}><inlineCode parentName="a">{`PathItems.add`}</inlineCode></a>{` method by providing `}<inlineCode parentName="li">{`PathPointInfo`}</inlineCode>{` and `}<inlineCode parentName="li">{`SubPathInfo`}</inlineCode>{` objects. Constructors for them can be found in `}<a parentName="li" {...{
          "href": "../classes/photoshop/"
        }}>{`app`}</a>{` object.`}</li>
    </ul>
    <h3 {...{
      "id": "load-on-launch-support"
    }}>{`Load on Launch support`}</h3>
    <p>{`You can now set your plugin manifest to load your plugin on app launch. See `}<a parentName="p" {...{
        "href": "../../guides/uxp_guide/uxp-misc/manifest-v4/photoshop-manifest/"
      }}>{`Photoshop Manifest`}</a>{` for details.`}</p>
    <h3 {...{
      "id": "batchplay-development-helper-copy-as-javascript"
    }}>{`batchPlay development helper: Copy as Javascript`}</h3>
    <p>{`It is now possible to use the Photoshop Actions panel to help build your plugin. Record valid actions, and let Photoshop generate readable and valid batchPlay to use in your UXP plugin. See the `}<a parentName="p" {...{
        "href": "../media/batchplay.md#accepted-action-descriptors"
      }}>{`batchPlay documentation`}</a>{` under "Accepted action descriptors".`}</p>
    <h3 {...{
      "id": "core-event-useridle"
    }}>{`Core event: "userIdle"`}</h3>
    <p>{`This event is generated when Photoshop detects that a user becomes idle, while Photoshop is the foreground application. A plugin must specify a number of idle seconds, and then may choose to batch process-intensive tasks. See `}<a parentName="p" {...{
        "href": "../media/photoshopcore/"
      }}>{`photoshopCore`}</a>{`.`}</p>
    <h3 {...{
      "id": "interactive-mode-for-executeasmodal"
    }}>{`Interactive Mode for executeAsModal`}</h3>
    <p>{`As an alterative to a UI-blocking progress bar when a plugin is within a Modal Execution scope, `}<inlineCode parentName="p">{`interactiveMode`}</inlineCode>{` can be requested to allow for user interaction in specific circumstances. See `}<a parentName="p" {...{
        "href": "../media/executeasmodal#interactive-mode/"
      }}>{`Interactive Mode`}</a>{`.`}</p>
    <h2 {...{
      "id": "photoshop-232-february-2022"
    }}>{`Photoshop 23.2 (February 2022)`}</h2>
    <ul>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`setExecutionMode()`}</inlineCode></li>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`getPluginInfo()`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "photoshop-231-december-2022"
    }}>{`Photoshop 23.1 (December 2022)`}</h2>
    <ul>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`batchPlaySync()`}</inlineCode></li>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`validateReference()`}</inlineCode></li>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`isModal()`}</inlineCode></li>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`getCPUInfo()`}</inlineCode></li>
      <li parentName="ul">{`Core Module: `}<inlineCode parentName="li">{`getGPUInfo()`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "photoshop-230-october-2021"
    }}>{`Photoshop 23.0 (October 2021)`}</h2>
    <h3 {...{
      "id": "channel-support"
    }}>{`Channel support`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/channels/"
        }}>{`Channels collections`}</a>{` behave like other collections in the API. Channels further supports `}<inlineCode parentName="li">{`channels.add()`}</inlineCode>{` and `}<inlineCode parentName="li">{`channels.removeAll()`}</inlineCode>{`. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/channel"
        }}>{`Channel class`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Supports four types of channels, as listed in `}<a parentName="li" {...{
              "href": "../modules/constants/#channeltype"
            }}>{`Constants.ChannelType`}</a>{`: `}<inlineCode parentName="li">{`COMPONENT`}</inlineCode>{`, `}<inlineCode parentName="li">{`MASKEDAREA`}</inlineCode>{`, `}<inlineCode parentName="li">{`SELECTEDAREA`}</inlineCode>{`, `}<inlineCode parentName="li">{`SPOTCOLOR`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Adds the following Channel properties: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`type`}</inlineCode>{`, `}<inlineCode parentName="li">{`visible`}</inlineCode>{`, `}<inlineCode parentName="li">{`histogram`}</inlineCode>{`, `}<inlineCode parentName="li">{`color`}</inlineCode>{`, `}<inlineCode parentName="li">{`opacity`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Adds the following Channel methods: `}<inlineCode parentName="li">{`remove()`}</inlineCode>{`, `}<inlineCode parentName="li">{`merge()`}</inlineCode>{` (for ChannelType.SPOTCOLOR).`}</li>
          <li parentName="ul">{`Adds to the Document class: `}<inlineCode parentName="li">{`get compositeChannels`}</inlineCode>{`, `}<inlineCode parentName="li">{`get activeChannels`}</inlineCode>{`, `}<inlineCode parentName="li">{`set activeChannels`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "document-transformations"
    }}>{`Document Transformations`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Document.crop(bounds: Bounds, angle, width, height)`}</inlineCode>{`: Crops the document. The `}<inlineCode parentName="li">{`bounds`}</inlineCode>{` paramter is an array of four coordinates for the region remaining after cropping, `}{`[left, top, right, bottom]`}{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.resizeCanvas(width, height, Constants.AnchorPosition)`}</inlineCode>{`: Changes the size of the canvas to display more or less of the image but does not change the image size. See `}<inlineCode parentName="li">{`resizeImage`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.resizeImage(width, heigh, resolution, Constants.ResampleMethod, amount)`}</inlineCode>{`: Changes the size of the image. The `}<inlineCode parentName="li">{`amount`}</inlineCode>{` of noise value when using preserve details (Range: 0 1 100).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.rotateCanvas(angle)`}</inlineCode>{`: Rotates the image and resizes the canvas to fit the resized image.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.trim(Constants.TrimType, top, left, bottom, right)`}</inlineCode>{`: If `}<inlineCode parentName="li">{`TrimType.TRANSPARENT`}</inlineCode>{`, trims the transparent area around the image on the specified sides of the canvas. Trims surround areas that color match the top left corner or bottom right corner pixel if `}<inlineCode parentName="li">{`TrimType.TOPLEFT`}</inlineCode>{` or `}<inlineCode parentName="li">{`TrimType.BOTTOMRIGHT`}</inlineCode>{` is specified, respectively.`}</li>
    </ul>
    <h3 {...{
      "id": "layer-cut-and-copy"
    }}>{`Layer cut and copy`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.cut()`}</inlineCode>{`: Cut the pixels out of the layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.copy(merge: boolean)`}</inlineCode>{`: Copy the layer or if a selection is present the merge option will copy from multiple layers..`}</li>
    </ul>
    <h3 {...{
      "id": "layer-methods"
    }}>{`Layer Methods`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.duplicate(relativeObject?: Document | Layer, insertionLocation?: Constants.ElementPlacement, name?: string)`}</inlineCode>{`: Duplicates the layer, creating a copy above it in layer stack and returns the newly created layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.delete()`}</inlineCode>{`: Deletes this layer from the document.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.link()`}</inlineCode>{`: Creates a link between this layer and the target layer if not already linked and returns a list of layers linked to this layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.unlink()`}</inlineCode>{`: Unlinks the layer from any existing links.`}</li>
    </ul>
    <h3 {...{
      "id": "document-paste"
    }}>{`Document paste`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`document.paste(intoSelection: boolean)`}</inlineCode>{`: Pastes the contents of the clipboard into the document. If the optional argument is set to true and a selection is active, the contents are pasted into the selection.`}</li>
    </ul>
    <h3 {...{
      "id": "document-methods"
    }}>{`Document Methods`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Document.flatten()`}</inlineCode>{`: Merges all visible layers in the document into a single layer.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.mergeVisibleLayers()`}</inlineCode>{`: Flattens all visible layers in the document`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.revealAll()`}</inlineCode>{`: Expands the document to show clipped sections.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.rasterizeAllLayers()`}</inlineCode>{`: Rasterizes all layers.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.splitChannels()`}</inlineCode>{`: Splits the document channels into separate, single-channel documents.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Document.duplicate()`}</inlineCode>{`: Creates a copy of the document, switching to the copy.`}</li>
    </ul>
    <h3 {...{
      "id": "solidcolor-api"
    }}><a parentName="h3" {...{
        "href": "../classes/solidcolor"
      }}>{`SolidColor`}</a>{` API`}</h3>
    <ul>
      <li parentName="ul">{`Add require('photoshop').app.SolidColor constructor, which allows for creation of SolidColor objects.`}</li>
      <li parentName="ul">{`APIs that accept colors now also accept SolidColor objects.`}
        <ul parentName="li">
          <li parentName="ul">{`app.foregroundColor and app.backgroundColor.`}</li>
          <li parentName="ul">{`app.documents.add `}<inlineCode parentName="li">{`fillColor`}</inlineCode>{` property.`}</li>
          <li parentName="ul">{`Document.saveAs methods that use colors.`}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`require('photoshop').core.convertColor`}</inlineCode>{` low level API added, used internally by SolidColor. It uses app color
settings to convert between color spaces.`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-beta-build-65-sep-3-2021-230"
    }}>{`Adobe Photoshop Beta Build 65: Sep 3, 2021 (23.0)`}</h2>
    <h3 {...{
      "id": "layer-transformations"
    }}>{`Layer Transformations`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`layer.rotate(angle: number, anchor?: Constants.AnchorPosition)`}</inlineCode>{`: Rotate by `}<inlineCode parentName="li">{`angle`}</inlineCode>{` about `}<inlineCode parentName="li">{`anchor`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.scale(width: number, height: number, anchor?: Constants.AnchorPosition)`}</inlineCode>{`: Scale layer by `}<inlineCode parentName="li">{`width`}</inlineCode>{` percent and `}<inlineCode parentName="li">{`height`}</inlineCode>{` percent of the image, relative to `}<inlineCode parentName="li">{`anchor`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`layer.translate(horizontal: number, vertical: number)`}</inlineCode>{`: Translate layer by `}<inlineCode parentName="li">{`horizontal`}</inlineCode>{` and `}<inlineCode parentName="li">{`vertical`}</inlineCode>{` offset (by pixel or percent).`}</li>
    </ul>
    <h3 {...{
      "id": "appfonts-and-textfont-classes"
    }}>{`app.fonts and TextFont classes`}</h3>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`app.fonts`}</inlineCode>{` getter: Returns all installed fonts`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`TextFont`}</inlineCode>{` class and `}<inlineCode parentName="li">{`TextFonts`}</inlineCode>{` collection class`}</li>
    </ul>
    <h3 {...{
      "id": "document-properties"
    }}>{`Document Properties`}</h3>
    <ul>
      <li parentName="ul">{`Document.bitsPerChannel (read-write): The bits per color channel (type Constants.BitsPerChannelType)`}</li>
      <li parentName="ul">{`Document.cloudDocument (read only): This document is in the Adobe Creative Cloud (type boolean)`}</li>
      <li parentName="ul">{`Document.cloudWorkAreaDirectory (read only): Local directory for this cloud document (type string)`}</li>
      <li parentName="ul">{`Document.histogram (read only): A histogram containing the number of pixels at each color intensity level for the composite channel. The array contains 256 members.`}</li>
      <li parentName="ul">{`Document.name (read only): The name of the document (type string)`}</li>
      <li parentName="ul">{`Document.path (read only): Full file system path to this document, or the identifier if it is a cloud document (type string)`}</li>
      <li parentName="ul">{`Document.quickMaskMode (read-write): The state of Quick Mask mode. If true, the app is in Quick Mask mode (type boolean)`}</li>
      <li parentName="ul">{`Document.saved (read only): True if the document has been saved since the last change (type boolean)`}</li>
    </ul>
    <h3 {...{
      "id": "document-color-profile-apis"
    }}>{`Document Color Profile APIs`}</h3>
    <ul>
      <li parentName="ul">{`Document.mode property (read only)`}</li>
      <li parentName="ul">{`Document.colorProfileType and Document.colorProfileName properties (read-write)`}</li>
      <li parentName="ul">{`Document.changeMode(), Document.convertProfile(), and Document.trap() methods`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-beta-build-64-aug-6-2021-230"
    }}>{`Adobe Photoshop Beta Build 64: Aug 6, 2021 (23.0)`}</h2>
    <h3 {...{
      "id": "documentsuspendhistory"
    }}><a parentName="h3" {...{
        "href": "../classes/document/#suspendhistory"
      }}>{`Document.suspendHistory`}</a></h3>
    <p>{`This API wraps around `}<a parentName="p" {...{
        "href": "https://adobe.ly/psmodal"
      }}><inlineCode parentName="a">{`core.executeAsModal`}</inlineCode></a>{` within a single document to make it easier for you to run your code under a single history state. Unlike it's ExtendScript predecessor, this method takes a Javascript function, rather than a script string.`}</p>
    <h3 {...{
      "id": "layer-move-apis"
    }}><a parentName="h3" {...{
        "href": "../classes/layer/#move"
      }}>{`Layer Move APIs`}</a></h3>
    <ul>
      <li parentName="ul">{`Arrange layers with `}<inlineCode parentName="li">{`layer.move(targetLayer, Constants.ElementPlacement)`}</inlineCode>{`. Added `}<inlineCode parentName="li">{`Constants.ElementPlacement`}</inlineCode>{` for moving layers: `}<inlineCode parentName="li">{`"placeAfter"`}</inlineCode>{` (place below a layer, below group if group layer), `}<inlineCode parentName="li">{`"placeBefore"`}</inlineCode>{` (place above a layer, above group if group layer), and `}<inlineCode parentName="li">{`"placeInside"`}</inlineCode>{` (inside group layers only).`}</li>
      <li parentName="ul">{`Send a layer to the back or bring a layer to the front with `}<inlineCode parentName="li">{`layer.sendToBack()`}</inlineCode>{` and `}<inlineCode parentName="li">{`layer.bringToFront()`}</inlineCode>{`. If `}<inlineCode parentName="li">{`layer`}</inlineCode>{` is within a group, the commands will operate within that group.`}</li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-prerelease-build-61-jun-24-2021-225"
    }}>{`Adobe Photoshop Prerelease Build 61: Jun 24, 2021 (22.5)`}</h2>
    <h3 {...{
      "id": "updated-dom-api"
    }}>{`Updated DOM API`}</h3>
    <ul>
      <li parentName="ul">{`New `}<inlineCode parentName="li">{`Layers`}</inlineCode>{` collection, the old `}<inlineCode parentName="li">{`layerTree`}</inlineCode>{` object is now called `}<inlineCode parentName="li">{`layers`}</inlineCode>{` but as a collection using the JavaScript Proxy object. See below for introduction of collections. The old `}<inlineCode parentName="li">{`layers`}</inlineCode>{` behavior is no longer part of the DOM. Most scripts can be updated to replace `}<inlineCode parentName="li">{`layerTree`}</inlineCode>{` with `}<inlineCode parentName="li">{`layers`}</inlineCode>{`.  A document and a layer, if the layer kind is group, will have a valid `}<inlineCode parentName="li">{`layers`}</inlineCode>{` collection. A `}<inlineCode parentName="li">{`pixel`}</inlineCode>{` layer will have `}<inlineCode parentName="li">{`null`}</inlineCode>{` for the `}<inlineCode parentName="li">{`layers`}</inlineCode>{` property. A collection has the same semantics as an Array. The additional ExtendScript routines of `}<inlineCode parentName="li">{`add`}</inlineCode>{` and `}<inlineCode parentName="li">{`getByName`}</inlineCode>{` are also available. Use `}<inlineCode parentName="li">{`LayerKind.GROUP`}</inlineCode>{` on a `}<inlineCode parentName="li">{`layer`}</inlineCode>{` to see if any `}<inlineCode parentName="li">{`layers`}</inlineCode>{` are available.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`GroupLayer`}</inlineCode>{` has been removed. Use `}<inlineCode parentName="li">{`activeLayer.kind === LayerKind.GROUP`}</inlineCode>{` instead.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Constants.LayerKind`}</inlineCode>{`, similar to ExtendScript version, but with "group" layer addition.`}</li>
      <li parentName="ul">{`Layer APIs`}
        <ul parentName="li">
          <li parentName="ul"><em parentName="li">{`new`}</em>{` locked, read-only way to tell if any of the properties are locked`}</li>
          <li parentName="ul">{`allLocked, pixelsLocked, positionLocked, transparentPixelsLocked properties`}</li>
          <li parentName="ul">{`Some existing properties exposed in documentation`}</li>
        </ul>
      </li>
    </ul>
    <h2 {...{
      "id": "adobe-photoshop-prerelease-build-60-jun-10-2021-225"
    }}>{`Adobe Photoshop Prerelease Build 60: Jun 10, 2021 (22.5)`}</h2>
    <p>{`This update contains changes that were made since the MAX 2020 (Adobe Photoshop 22.0) release.`}</p>
    <h3 {...{
      "id": "breaking-changes"
    }}>{`Breaking changes`}</h3>
    <p>{`To use the new set of APIs, your project's `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` must be updated to include the following under the `}<inlineCode parentName="p">{`host`}</inlineCode>{` object:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`    "data" : {
        "apiVersion": 2
    }
`}</code></pre>
    <p>{`Further, your `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` must use version 4:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  "manifestVersion": 4
`}</code></pre>
    <p>{`To roll back to the MAX 2020 version of the Photoshop DOM API, set the `}<inlineCode parentName="p">{`apiVersion`}</inlineCode>{` to 1.`}</p>
    <h3 {...{
      "id": "updated-dom-api-1"
    }}>{`Updated DOM API`}</h3>
    <ul>
      <li parentName="ul">{`Collections`}
        <ul parentName="li">
          <li parentName="ul">{`We're introducing collection classes built using `}<a parentName="li" {...{
              "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy"
            }}>{`Proxies`}</a>{`, to make it easier to interact with the DOM.`}</li>
          <li parentName="ul">{`Try iterating through all the documents as such: `}<inlineCode parentName="li">{`app.documents.forEach(h => console.log(h.title));`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/historystates/"
        }}>{`HistoryStates collections`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/historystate/"
        }}>{`HistoryState API`}</a>
        <ul parentName="li">
          <li parentName="ul">{`We've built HistoryStates to mirror ExtendScript functionality. `}<inlineCode parentName="li">{`length`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`, and `}<inlineCode parentName="li">{`getByName(name)`}</inlineCode>{` are provided. You can also use indices as such: `}<inlineCode parentName="li">{`activeDocument.historyStates[2]`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`HistoryState provides `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{`, `}<inlineCode parentName="li">{`snapshot`}</inlineCode>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/documents/"
        }}>{`Documents collections`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Documents also follows ExtendScript functionality. `}<inlineCode parentName="li">{`length`}</inlineCode>{`, `}<inlineCode parentName="li">{`add`}</inlineCode>{`, `}<inlineCode parentName="li">{`getByName(name)`}</inlineCode>{`, `}<inlineCode parentName="li">{`parent`}</inlineCode>{` are provided.`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../modules/constants/"
        }}>{`Constants module`}</a>{`, collecting all enumerations and constants used in DOM API. Accessible via `}<inlineCode parentName="li">{`require("photoshop").constants`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/photoshop/#createdocument"
        }}>{`app.createDocument / app.add`}</a>
        <ul parentName="li">
          <li parentName="ul">{`Create a document with no params for a default document, with a predefined preset, or by providing a host of `}<a parentName="li" {...{
              "href": "../objects/createoptions/documentcreateoptions/"
            }}>{`DocumentCreateOptions`}</a>{`).`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/document/#save"
        }}>{`document.save`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/document/#saveas"
        }}>{`document.saveAs`}</a>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`save`}</inlineCode>{` no longer infers the file type from the requested file name. Instead, it invokes a save dialog for unsaved files, and performs a save operation for saved, modified files.`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`saveAs`}</inlineCode>{` is provided for `}<inlineCode parentName="li">{`bmp`}</inlineCode>{`, `}<inlineCode parentName="li">{`gif`}</inlineCode>{`, `}<inlineCode parentName="li">{`jpg`}</inlineCode>{`, `}<inlineCode parentName="li">{`png`}</inlineCode>{`, `}<inlineCode parentName="li">{`psb`}</inlineCode>{`, `}<inlineCode parentName="li">{`psd`}</inlineCode>{` formats. Use SaveOptions objects to request specific saves (e.g. `}<a parentName="li" {...{
              "href": "../objects/photoshopsaveoptions/"
            }}>{`PhotoshopSaveOptions`}</a>{`).`}</li>
        </ul>
      </li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../classes/layer"
        }}>{`layer.id getter`}</a></li>
      <li parentName="ul">{`Updated `}<a parentName="li" {...{
          "href": "../classes/document/#createlayer"
        }}>{`document.createLayer`}</a>{` and `}<a parentName="li" {...{
          "href": "../classes/document/#createlayergroup"
        }}>{`document.createLayerGroup`}</a>
        <ul parentName="li">
          <li parentName="ul">{`With respective Create options: `}<a parentName="li" {...{
              "href": "../objects/createoptions/layercreateoptions/"
            }}>{`LayerCreateOptions`}</a>{` and `}<a parentName="li" {...{
              "href": "../objects/createoptions/grouplayercreateoptions"
            }}>{`GroupLayerCreateOptions`}</a>{`.`}</li>
          <li parentName="ul">{`Collection access updates for Layer and Layer-like items are planned for upcoming releases.`}</li>
        </ul>
      </li>
    </ul>
    <h4 {...{
      "id": "photoshop-core-updates"
    }}>{`Photoshop Core updates`}</h4>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "../media/executeasmodal/"
        }}>{`Execute as Modal`}</a>{` allows you to create plugins that can be guaranteed exclusive control over Photoshop during execution. It further allows for clear user feedback when this is occuring, and allows plugins to `}<inlineCode parentName="li">{`suspendHistory`}</inlineCode>{` and `}<inlineCode parentName="li">{`resumeHistory`}</inlineCode>{`.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      